import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface AuthGuardProps {
  requiredRole?: 'admin' | 'branch';
  redirectTo?: string;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ requiredRole, redirectTo = '/login' }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Optional debug
    // console.log('AuthGuard: loading:', loading, 'isAuthenticated:', isAuthenticated, 'user:', user);
  }, [loading, isAuthenticated, user]);

  // Still checking auth
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-primary border-r-transparent" />
      </div>
    );
  }

  // Not authenticated — only redirect if not already on login page
  if (!isAuthenticated) {
    if (location.pathname !== '/login') {
      return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }
    return <Outlet />;
  }

  // Role mismatch (but allow admin to override)
  if (requiredRole && user?.role !== requiredRole) {
    if (user.role === 'admin') {
      return <Outlet />;
    }
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
};